import { Icon } from "@iconify/react";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  SwipeableDrawer,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import NearbyStoresMobile from "./nearbyStores/products.nearbyStoresMobile";

const CHAT_HISTORY_KEY = "chatHistory";

const ChatMobile = ({ open, onClose, id, product }) => {
  const searchRef = useRef(null);
  const [messages, setMessages] = useState([
    {
      text: "Hi! Welcome to our customer service. How can I assist you today?",
      sender: "bot",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [botIsLoading, setBotIsLoading] = useState(false);
  const [nearbyStoresOpen, setNearbyStoresOpen] = useState(false);
  const [zipcode, setZipcode] = useState(""); 
  const [locationDenied, setLocationDenied] = useState(false);

  useEffect(() => {
    searchRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const savedChatData = JSON.parse(localStorage.getItem(CHAT_HISTORY_KEY));
    if (savedChatData && savedChatData.expiry > Date.now()) {
      if (savedChatData.upc === id) {
        setMessages(savedChatData.messages);
      }
    }
  }, [id]);

  useEffect(() => {
    if (messages.length > 1) {
      const chatData = {
        upc: id,
        messages: messages,
        expiry: Date.now() + 3600000, 
      };
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatData));
    }
  }, [messages, id]);

  const handleSendMessage = () => {
    if (inputValue.trim() !== "") {
      const userMessage = { text: inputValue.trim(), sender: "user" };
      setInputValue("");
      setBotIsLoading(true);
      setMessages((prevMessages) => [...prevMessages, userMessage]);

      const purchaseKeywords = [
        "where can i buy", "buy", "purchase", "order", "get this",
        "find this", "available at", "shop", "store", "availability", "locate", "where"
      ];
      if (purchaseKeywords.some(keyword => userMessage.text.toLowerCase().includes(keyword))) {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: "nearby_stores",
            sender: "bot",
            text: "You can see nearby places where you can find this product.",
          },
        ]);
        setBotIsLoading(false);
      } else {
        openaiResponse(
          userMessage.text,
          product.name,
          product.description,
          product.nutrition,
          product.ingredients
        )
          .then(() => setBotIsLoading(false))
          .catch((error) => {
            console.error("openai error:", error);
            setBotIsLoading(false);
          });
      }
    }
  };

  const handleOpenNearbyStores = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setNearbyStoresOpen(true);
        },
        (error) => {
          console.error("Geolocation error:", error);
          setLocationDenied(true);
          setNearbyStoresOpen(true);
        }
      );
    } else {
      setLocationDenied(true);
      setNearbyStoresOpen(true);
    }
  };

  const handleZipSubmit = () => {
    if (zipcode.trim() !== "") {
      setLocationDenied(false);
    }
  };

  const openaiResponse = async (question, productName, description, nutrition, ingredients) => {
    try {
      const response = await axios.post("/chat/openai", {
        productName,
        question,
        productDescription: description,
        nutrition,
        ingredients,
      });
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: response.data.response, sender: "bot" },
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <SwipeableDrawer anchor="bottom" open={open} onClose={onClose} onOpen={() => {}}>
      <Box
        sx={{
          maxHeight: "80vh",
          minHeight: "65vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFF",
          borderRadius: "15px 15px 0 0",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ textAlign: "left", color: "#205375" }}>
            Talk to <span style={{ color: "#EF4F26" }}>Product</span>
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Chat Messages */}
        <Box sx={{ flex: 1, overflowY: "auto", padding: "10px" }}>
          {messages.map((message, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: message.sender === "user" ? "flex-end" : "flex-start",
                mb: 1,
              }}
            >
              {message.type === "nearby_stores" ? (
                <Box
                  sx={{
                    backgroundColor: "#e0e0e0",
                    color: "#000",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    maxWidth: "55%",
                    mt: 1,
                  }}
                >
                  <Typography variant="body2">{message.text}</Typography>
                  <>
                    <IconButton
                      onClick={handleOpenNearbyStores}
                      sx={{
                        width: "194px",
                        height: "53px",
                        borderRadius: "30px",
                        background: "#E8E8E8",
                        gap: 1,
                        "&:hover": {
                          background: "#E8E8E8",
                          opacity: 1,
                        },
                        mt: 1,
                      }}
                    >
                      <Icon icon={"bx:store"} style={{ fontWeight: 800, color: "#205375" }} />
                      <Typography variant="h6" sx={{ color: "#205375" }}>
                        Stores Nearby
                      </Typography>
                    </IconButton>
                    <SwipeableDrawer
                      anchor="bottom"
                      open={nearbyStoresOpen}
                      onClose={() => setNearbyStoresOpen(false)}
                      onOpen={() => setNearbyStoresOpen(true)}
                    >
                      <Box sx={{ mt: 2, maxHeight: "60vh", minHeight: "65vh", py: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 3,
                            position: "fixed",
                            width: "100%",
                            transform: "translateY(-2vh)",
                          }}
                        >
                          <Box sx={{ px: 2 }}>
                            <Typography
                              variant="h5"
                              color="#205375"
                              component="div"
                              sx={{ fontWeight: 600, textAlign: "left", mb: 2 }}
                            >
                              Nearby <span style={{ color: "#EF4F26" }}>Stores</span>
                            </Typography>
                          </Box>
                          <Box sx={{ zIndex: 1, px: 2 }}>
                            <span
                              style={{
                                border: "1px solid #205375",
                                padding: "5px 10px",
                                borderRadius: 15,
                              }}
                            >
                              <Button onClick={() => setNearbyStoresOpen(false)}>Close</Button>
                            </span>
                          </Box>
                        </Box>

                        {locationDenied && (
                          <Box sx={{ p: 3 }}>
                            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                              Location services are disabled or permission is denied. Please enter a ZIP code:
                            </Typography>
                            <TextField
                              label="Enter ZIP Code"
                              variant="outlined"
                              fullWidth
                              value={zipcode}
                              onChange={(e) => setZipcode(e.target.value)}
                              sx={{ mb: 2 }}
                            />
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              onClick={handleZipSubmit}
                              disabled={!zipcode.trim()}
                            >
                              Search
                            </Button>
                          </Box>
                        )}

                        {!locationDenied && (
                          <NearbyStoresMobile product={product} open={nearbyStoresOpen} zipcode={zipcode} />
                        )}
                      </Box>
                    </SwipeableDrawer>
                  </>
                </Box>
              ) : (
                <Box
                  sx={{
                    backgroundColor: message.sender === "user" ? "#0073b1" : "#E0E0E0",
                    color: message.sender === "user" ? "white" : "black",
                    padding: "8px 12px",
                    borderRadius: "12px",
                    maxWidth: "60%",
                  }}
                >
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      paddingLeft: "6px",
                      paddingRight: "8px",
                      "& ul": { paddingLeft: "20px" },
                      "& li": { marginBottom: "4px" },
                    }}
                  >
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
          {botIsLoading && (
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="body2">...</Typography>
            </Box>
          )}
          <div ref={searchRef} />
        </Box>

        {/* Chat Input */}
        <TextField
          sx={{
            mb: 2,
            padding: "0 10px",
          }}
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSendMessage} color="primary">
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </SwipeableDrawer>
  );
};

export default ChatMobile;
